import YAML from 'yaml'
import type { RenderData } from '../assetTypes'
import { collectAssetReferencesFromValue, extractAssetReferences } from '../shared/assetReferences'
import {
  cloneJsonObject,
  cloneJsonValue,
  normalizeJsonObject,
  type JsonObject,
  type JsonValue,
} from '../shared/jsonValues'

const ASSET_REFERENCE_PATTERN = /^asset:\/\/([0-9a-fA-F-]{36})$/

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isJsonObject = (value: JsonValue | undefined): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeFeatureName = (value: string) => value.trim().toLowerCase()

const normalizeAssetReference = (value: string) => {
  const trimmed = value.trim()
  const match = trimmed.match(ASSET_REFERENCE_PATTERN)
  if (!match?.[1]) {
    throw new Error(`Asset reference "${value}" must use the form asset://<uuid>.`)
  }
  return `asset://${match[1].toLowerCase()}`
}

const cloneResolvedFeature = (feature: ResolvedConfigFeature): ResolvedConfigFeature => ({
  name: feature.name,
  asset: feature.asset,
  params: cloneJsonObject(feature.params),
})

const deepMergeJsonValues = (base: JsonValue | undefined, override: JsonValue | undefined): JsonValue | undefined => {
  if (override === undefined) {
    return base === undefined ? undefined : cloneJsonValue(base)
  }

  if (isJsonObject(base) && isJsonObject(override)) {
    return deepMergeJsonObjects(base, override)
  }

  return cloneJsonValue(override)
}

const deepMergeJsonObjects = (base: JsonObject, override: JsonObject): JsonObject => {
  const merged: JsonObject = {}
  const keys = new Set([...Object.keys(base), ...Object.keys(override)])

  keys.forEach((key) => {
    const mergedValue = deepMergeJsonValues(base[key], override[key])
    if (mergedValue !== undefined) merged[key] = mergedValue
  })

  return merged
}

export type ConfigFeatureDefinition = {
  name: string
  asset: string
  params: JsonObject
}

export type ConfigDefinition = {
  root: string
  features: ConfigFeatureDefinition[]
}

export type ResolvedConfigFeature = {
  name: string
  asset: string
  params: JsonObject
}

export type ResolveConfigRouteInput = {
  feature: string
  params?: JsonObject
}

export type ResolvedConfigDefinition = {
  root: ResolvedConfigFeature
  resolveRoute: (input: ResolveConfigRouteInput) => ResolvedConfigFeature
}

const parseFeatureDefinition = (value: unknown, index: number): ConfigFeatureDefinition => {
  if (!isRecord(value)) {
    throw new Error(`Feature entry #${index + 1} must be a YAML object.`)
  }

  const name = typeof value.name === 'string' ? value.name.trim() : ''
  if (!name) {
    throw new Error(`Feature entry #${index + 1} must define a non-empty name.`)
  }

  const rawAsset = typeof value.asset === 'string' ? value.asset.trim() : ''
  if (!rawAsset) {
    throw new Error(`Feature "${name}" must define an asset reference.`)
  }

  const rawParams = value.params
  if (rawParams !== undefined && rawParams !== null && !isRecord(rawParams)) {
    throw new Error(`Feature "${name}" params must be a YAML object.`)
  }

  return {
    name,
    asset: normalizeAssetReference(rawAsset),
    params: normalizeJsonObject(rawParams as Record<string, unknown> | null | undefined),
  }
}

export const parseConfigDefinition = (content: string): ConfigDefinition => {
  const parsed = YAML.parse(String(content || ''))
  if (!isRecord(parsed)) {
    throw new Error('Config payload must be a YAML object.')
  }

  const root = typeof parsed.root === 'string' ? parsed.root.trim() : ''
  if (!root) {
    throw new Error('Config payload must define a non-empty "root" feature name.')
  }

  if (!Array.isArray(parsed.features)) {
    throw new Error('Config payload must define "features" as a YAML list.')
  }

  const features = parsed.features.map((entry, index) => parseFeatureDefinition(entry, index))
  const seenNames = new Set<string>()
  features.forEach((feature) => {
    const normalized = normalizeFeatureName(feature.name)
    if (seenNames.has(normalized)) {
      throw new Error(`Feature names must be unique. Duplicate feature "${feature.name}".`)
    }
    seenNames.add(normalized)
  })

  return {
    root,
    features,
  }
}

export const resolveConfigDefinition = (content: string): ResolvedConfigDefinition => {
  const config = parseConfigDefinition(content)
  const featuresByName = new Map<string, ConfigFeatureDefinition>()
  config.features.forEach((feature) => {
    featuresByName.set(normalizeFeatureName(feature.name), feature)
  })

  const resolvedCache = new Map<string, ResolvedConfigFeature>()
  const resolutionStack: string[] = []

  const resolveJsonValueWithConfig = (value: JsonValue, path: string): JsonValue => {
    if (Array.isArray(value)) {
      return value.map((entry, index) => resolveJsonValueWithConfig(entry, `${path}[${index}]`))
    }

    if (!isJsonObject(value)) {
      return cloneJsonValue(value)
    }

    const featureTarget = typeof value.feature === 'string' ? value.feature.trim() : ''
    const hasFeatureTarget = featureTarget.length > 0

    const resolvedRecord: JsonObject = {}
    Object.entries(value).forEach(([key, entry]) => {
      if (key === 'feature' || key === 'params') return
      resolvedRecord[key] = resolveJsonValueWithConfig(entry, `${path}.${key}`)
    })

    if (!hasFeatureTarget) {
      return resolvedRecord
    }

    const rawParams = value.params
    if (rawParams !== undefined && rawParams !== null && !isRecord(rawParams)) {
      throw new Error(`"${path}.params" must be a YAML object when "${path}.feature" is used.`)
    }

    const localParams = normalizeJsonObject(rawParams as Record<string, unknown> | null | undefined)
    const referencedFeature = featuresByName.get(normalizeFeatureName(featureTarget))

    let resolvedFeatureTarget = featureTarget
    let mergedParams = localParams

    if (referencedFeature) {
      const resolvedNamedFeature = resolveFeature(referencedFeature.name)
      resolvedFeatureTarget = resolvedNamedFeature.asset
      mergedParams = deepMergeJsonObjects(resolvedNamedFeature.params, localParams)
    } else if (ASSET_REFERENCE_PATTERN.test(featureTarget)) {
      resolvedFeatureTarget = normalizeAssetReference(featureTarget)
    } else {
      throw new Error(`"${path}.feature" must be an asset://<uuid> reference or the name of another feature.`)
    }

    resolvedRecord.feature = resolvedFeatureTarget
    if (Object.keys(mergedParams).length > 0 || rawParams !== undefined || referencedFeature) {
      resolvedRecord.params = resolveJsonValueWithConfig(mergedParams, `${path}.params`) as JsonObject
    }

    return resolvedRecord
  }

  const resolveFeature = (featureName: string): ResolvedConfigFeature => {
    const normalizedFeatureName = normalizeFeatureName(featureName)
    const cached = resolvedCache.get(normalizedFeatureName)
    if (cached) return cloneResolvedFeature(cached)

    const feature = featuresByName.get(normalizedFeatureName)
    if (!feature) {
      throw new Error(`Unknown feature "${featureName}".`)
    }

    if (resolutionStack.includes(normalizedFeatureName)) {
      const cycle = [...resolutionStack, normalizedFeatureName]
        .map((entry) => featuresByName.get(entry)?.name ?? entry)
        .join(' -> ')
      throw new Error(`Feature reference cycle detected: ${cycle}.`)
    }

    resolutionStack.push(normalizedFeatureName)
    try {
      const resolved: ResolvedConfigFeature = {
        name: feature.name,
        asset: feature.asset,
        params: resolveJsonValueWithConfig(feature.params, `features.${feature.name}.params`) as JsonObject,
      }
      resolvedCache.set(normalizedFeatureName, resolved)
      return cloneResolvedFeature(resolved)
    } finally {
      resolutionStack.pop()
    }
  }

  const resolveRoute = ({ feature, params }: ResolveConfigRouteInput): ResolvedConfigFeature => {
    const trimmedFeature = feature.trim()
    if (!trimmedFeature) {
      throw new Error('Route feature must be a non-empty string.')
    }

    const rawParams = params as Record<string, unknown> | null | undefined
    const localParams = normalizeJsonObject(rawParams)
    const referencedFeature = featuresByName.get(normalizeFeatureName(trimmedFeature))

    if (referencedFeature) {
      const resolvedFeature = resolveFeature(referencedFeature.name)
      return {
        name: resolvedFeature.name,
        asset: resolvedFeature.asset,
        params: resolveJsonValueWithConfig(
          deepMergeJsonObjects(resolvedFeature.params, localParams),
          `route.${referencedFeature.name}.params`
        ) as JsonObject,
      }
    }

    if (ASSET_REFERENCE_PATTERN.test(trimmedFeature)) {
      return {
        name: trimmedFeature,
        asset: normalizeAssetReference(trimmedFeature),
        params: resolveJsonValueWithConfig(localParams, `route.${trimmedFeature}.params`) as JsonObject,
      }
    }

    throw new Error(
      `Route feature "${trimmedFeature}" must be an asset://<uuid> reference or the name of another feature.`
    )
  }

  const rootFeature = featuresByName.get(normalizeFeatureName(config.root))
  if (!rootFeature) {
    throw new Error(`Config root "${config.root}" does not match any feature in the "features" list.`)
  }

  return {
    root: resolveFeature(rootFeature.name),
    resolveRoute,
  }
}

export const collectConfigAssetReferences = (content: string, renderData?: RenderData) => {
  const matches = new Set<string>()
  renderData?.entity?.fields?.forEach((field) => {
    if (typeof field.value === 'string') {
      extractAssetReferences(field.value, matches)
    }
  })

  try {
    const resolved = resolveConfigDefinition(content)
    extractAssetReferences(resolved.root.asset, matches)
    collectAssetReferencesFromValue(resolved.root.params).forEach((reference) => matches.add(reference))
  } catch {
    // The renderer will surface parse/validation errors. Keep asset collection best-effort.
  }

  return Array.from(matches)
}
